import { createContext, useContext } from "react";
import { UI } from "./ui";

export const LANGS = ["en", "nl", "fr", "de"];

export function tx(lang, str) {
  if (!str || lang === "en" || typeof str !== "string") return str;
  const m = UI[lang];
  return (m && m[str]) || str;
}

// Module-level active language. The language lives in the URL (/:lang), so a
// language switch always triggers a full route re-render. LangShell keeps this
// in sync, which makes a plain (non-hook) tr() safe to use at render time.
let ACTIVE_LANG = "en";
export function setActiveLang(l) {
  ACTIVE_LANG = l;
}
export function tr(s) {
  return tx(ACTIVE_LANG, s);
}

const en = {
  nav: {
    home: "Home", about: "About ESAIA", standards: "Standards", services: "Services",
    certification: "Certification", eri: "ERI", insights: "Insights", contact: "Contact",
    pricing: "Pricing", faq: "FAQ", verify: "Verify Certificate",
    ctaQuote: "Request a Quote", ctaEri: "Take the Free ERI",
  },
  footer: {
    tagline: "Built for a changing world.",
    colEsaia: "ESAIA", colCertification: "Certification", colServices: "Services",
    colStandards: "Standards", colResources: "Resources", colLegal: "Legal",
    mission: "Mission", independence: "Independence",
    individualCert: "Single-Entity AI Assessment", groupCert: "Group-Wide AI Assessment",
    recert: "Annual Recertification", certVerify: "Certificate Verification",
    aiAssurance: "AI Assurance", aiAgent: "AI Agent Assessment", aiSystem: "AI System Assessment",
    standard01: "Standards", privacy: "Privacy Policy", cookies: "Cookie Policy",
    terms: "Terms", certDisclaimer: "Certification Disclaimer",
    contact: "Contact",
    disclaimer: "ESAIA certification is a voluntary independent assurance assessment. ESAIA certification does not constitute governmental approval, regulatory authorization or a guarantee of legal compliance with the EU AI Act or any other legislation. Organizations remain responsible for determining and fulfilling their applicable legal and regulatory obligations.",
    rights: "All rights reserved.",
  },
  common: {
    requestQuote: "Request a Quote",
    startFreeEri: "Start the Free ERI",
    exploreCertification: "Explore ESAIA Certification",
    learnMore: "Learn more",
    free: "FREE",
    validity: "Certification validity: 12 months",
  },
};

const dict = {
  en,
  nl: {
    nav: {
      home: "Home", about: "Over ESAIA", standards: "Standaarden", services: "Diensten",
      certification: "Certificering", eri: "ERI", insights: "Inzichten", contact: "Contact",
      pricing: "Prijzen", faq: "FAQ", verify: "Certificaat verifiëren",
      ctaQuote: "Offerte aanvragen", ctaEri: "Doe de gratis ERI",
    },
    footer: {
      ...en.footer,
      colServices: "Diensten",
      colResources: "Bronnen",
      colLegal: "Juridisch",
      mission: "Missie",
      independence: "Onafhankelijkheid",
      individualCert: "Single-Entity AI Assessment",
      groupCert: "Group-Wide AI Assessment",
      recert: "Jaarlijkse hercertificering",
      certVerify: "Certificaatverificatie",
      aiAssurance: "AI Assurance",
      aiAgent: "AI-agentbeoordeling",
      aiSystem: "AI-systeembeoordeling",
      standard01: "Standaarden",
      privacy: "Privacybeleid",
      cookies: "Cookiebeleid",
      terms: "Voorwaarden",
      certDisclaimer: "Certificeringsdisclaimer",
      contact: "Contact",
      disclaimer: "ESAIA certificering is een vrijwillige, onafhankelijke assurance beoordeling. ESAIA certificering vormt geen overheidsgoedkeuring, regelgevende autorisatie of garantie van wettelijke naleving van de EU AI Act of enige andere wetgeving. Organisaties blijven zelf verantwoordelijk voor het bepalen en nakomen van hun wettelijke verplichtingen.",
      rights: "Alle rechten voorbehouden.",
    },
    common: {
      requestQuote: "Offerte aanvragen",
      startFreeEri: "Start de gratis ERI",
      exploreCertification: "Ontdek ESAIA certificering",
      learnMore: "Meer weten",
      free: "GRATIS",
      validity: "Certificering geldig: 12 maanden",
    },
  },
  fr: {
    nav: {
      home: "Accueil", about: "À propos", standards: "Normes", services: "Services",
      certification: "Certification", eri: "ERI", insights: "Analyses", contact: "Contact",
      pricing: "Tarifs", faq: "FAQ", verify: "Vérifier un certificat",
      ctaQuote: "Demander un devis", ctaEri: "Faire le ERI gratuit",
    },
    footer: {
      ...en.footer,
      colServices: "Services",
      colResources: "Ressources",
      colLegal: "Mentions légales",
      mission: "Mission",
      independence: "Indépendance",
      individualCert: "Évaluation IA d'entité unique",
      groupCert: "Évaluation IA à l'échelle du groupe",
      recert: "Recertification annuelle",
      certVerify: "Vérification de certificat",
      aiAssurance: "Assurance IA",
      aiAgent: "Évaluation d'agents IA",
      aiSystem: "Évaluation de systèmes IA",
      standard01: "Normes",
      privacy: "Politique de confidentialité",
      cookies: "Politique relative aux cookies",
      terms: "Conditions",
      certDisclaimer: "Clause de certification",
      contact: "Contact",
      disclaimer: "La certification ESAIA est une évaluation d'assurance indépendante et volontaire. Elle ne constitue ni une approbation gouvernementale, ni une autorisation réglementaire, ni une garantie de conformité légale avec le règlement européen sur l'IA ou toute autre législation. Les organisations restent responsables de leurs obligations légales et réglementaires.",
      rights: "Tous droits réservés.",
    },
    common: {
      requestQuote: "Demander un devis",
      startFreeEri: "Commencer le ERI gratuit",
      exploreCertification: "Découvrir la certification ESAIA",
      learnMore: "En savoir plus",
      free: "GRATUIT",
      validity: "Validité de la certification : 12 mois",
    },
  },
  de: {
    nav: {
      home: "Start", about: "Über ESAIA", standards: "Standards", services: "Leistungen",
      certification: "Zertifizierung", eri: "ERI", insights: "Einblicke", contact: "Kontakt",
      pricing: "Preise", faq: "FAQ", verify: "Zertifikat prüfen",
      ctaQuote: "Angebot anfordern", ctaEri: "Kostenlosen ERI starten",
    },
    footer: {
      ...en.footer,
      colServices: "Leistungen",
      colResources: "Ressourcen",
      colLegal: "Rechtliches",
      mission: "Mission",
      independence: "Unabhängigkeit",
      individualCert: "KI-Bewertung für einzelne Einheiten",
      groupCert: "Konzernweite KI-Bewertung",
      recert: "Jährliche Rezertifizierung",
      certVerify: "Zertifikatsverifizierung",
      aiAssurance: "KI-Assurance",
      aiAgent: "KI-Agenten-Bewertung",
      aiSystem: "KI-System-Bewertung",
      standard01: "Standards",
      privacy: "Datenschutzerklärung",
      cookies: "Cookie-Richtlinie",
      terms: "Bedingungen",
      certDisclaimer: "Zertifizierungs-Haftungsausschluss",
      contact: "Kontakt",
      disclaimer: "Die ESAIA Zertifizierung ist eine freiwillige, unabhängige Assurance Prüfung. Sie stellt keine behördliche Genehmigung, regulatorische Zulassung oder Garantie der gesetzlichen Konformität mit der EU KI Verordnung oder anderen Rechtsvorschriften dar. Organisationen bleiben für die Erfüllung ihrer rechtlichen Pflichten selbst verantwortlich.",
      rights: "Alle Rechte vorbehalten.",
    },
    common: {
      requestQuote: "Angebot anfordern",
      startFreeEri: "Kostenlosen ERI starten",
      exploreCertification: "ESAIA Zertifizierung entdecken",
      learnMore: "Mehr erfahren",
      free: "KOSTENLOS",
      validity: "Zertifizierung gültig: 12 Monate",
    },
  },
};

const LanguageContext = createContext({ lang: "en", t: dict.en });

export const LanguageProvider = LanguageContext.Provider;

export function useLanguage() {
  return useContext(LanguageContext);
}

export function useTr() {
  const { lang } = useLanguage();
  return (s) => tx(lang, s);
}

export function getStrings(lang) {
  return dict[lang] || dict.en;
}
